import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Typography,
  Spin,
  Empty,
  Descriptions,
  Button,
  Tooltip,
  Select,
  Space,
} from "antd";
import {
  HeartOutlined,
  CloudServerOutlined,
  SafetyOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { apiService } from "@/services/api";
import type { PaymentProviderConfig } from "@/types";

const { Title, Text } = Typography;

export const TechnicalSettingsPage: React.FC = () => {
  const [healthData, setHealthData] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(false);
  const [providers, setProviders] = useState<PaymentProviderConfig[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [contents, setContents] = useState<any[]>([]);
  const [contentsLoading, setContentsLoading] = useState(false);
  const [processingFilter, setProcessingFilter] = useState<string | undefined>();

  const loadHealth = async () => {
    setHealthLoading(true);
    setHealthError(false);
    try {
      // Passe par apiService, donc par l'intercepteur qui renouvelle le jeton.
      // L'ancien appel utilisait un axios brut avec le jeton lu dans
      // localStorage : il echouait au bout de cinq minutes.
      setHealthData(await apiService.getSystemHealth());
    } catch {
      setHealthError(true);
      setHealthData(null);
    } finally {
      setHealthLoading(false);
    }
  };

  const loadProviders = async () => {
    setProvidersLoading(true);
    try {
      const res = await apiService.getPaymentProviders();
      setProviders(Array.isArray(res) ? res : []);
    } catch {
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  };

  const loadContentsProcessing = useCallback(async () => {
    setContentsLoading(true);
    try {
      const res = await apiService.getContents(undefined, 1, 50);
      setContents(res.data || []);
    } catch {
      setContents([]);
    } finally {
      setContentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    loadProviders();
    loadContentsProcessing();
  }, []);

  // Un service par ligne, tel que le serveur les a sondes. La page lisait
  // auparavant `components` : les composants internes du seul backoffice-bff.
  const healthComponents = (healthData?.services ?? []).map(
    (service: any) => ({
      key: service.name,
      name: service.name,
      status: service.status,
      responseMs: service.responseMs,
      details: service.detail ?? "—",
    })
  );

  const healthColumns = [
    {
      title: "Service",
      dataIndex: "name",
      key: "name",
      render: (val: string) => <Text strong>{val}</Text>,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status: string) =>
        status === "UP" ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            UP
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            DOWN
          </Tag>
        ),
    },
    {
      title: "Réponse",
      dataIndex: "responseMs",
      key: "responseMs",
      width: 110,
      render: (ms?: number) =>
        ms === undefined ? (
          <Text type="secondary">—</Text>
        ) : (
          // Au-dela d'une seconde, un service repond mais peine : c'est une
          // information utile avant qu'il ne tombe.
          <Text type={ms > 1000 ? "warning" : "secondary"}>{ms} ms</Text>
        ),
    },
    {
      title: "Détails",
      dataIndex: "details",
      key: "details",
      ellipsis: true,
    },
  ];

  const getProcessingStatusTag = (status?: string) => {
    const map: Record<string, { color: string; label: string }> = {
      PENDING: { color: "orange", label: "En attente" },
      PROCESSING: { color: "processing", label: "En cours" },
      COMPLETED: { color: "success", label: "Terminé" },
      FAILED: { color: "error", label: "Échoué" },
    };
    const config = status ? map[status] : null;
    return config ? <Tag color={config.color}>{config.label}</Tag> : <Tag>N/A</Tag>;
  };

  const filteredContents = processingFilter
    ? contents.filter(
        (c) => c.videoProcessingStatus === processingFilter || c.trailerProcessingStatus === processingFilter
      )
    : contents;

  const processingColumns = [
    {
      title: "Contenu",
      dataIndex: "title",
      key: "title",
      render: (val: string) => <Text strong>{val}</Text>,
    },
    {
      title: "Statut vidéo",
      dataIndex: "videoProcessingStatus",
      key: "videoProcessingStatus",
      render: (status: string) => getProcessingStatusTag(status),
    },
    {
      title: "Statut bande-annonce",
      dataIndex: "trailerProcessingStatus",
      key: "trailerProcessingStatus",
      render: (status: string) => getProcessingStatusTag(status),
    },
    {
      title: "Qualité",
      dataIndex: "videoQuality",
      key: "videoQuality",
      render: (val: string) => val || "--",
    },
    {
      title: "Résolution",
      dataIndex: "videoResolution",
      key: "videoResolution",
      render: (val: string) => val || "--",
    },
    {
      title: "Dernière MAJ",
      dataIndex: "lastVideoUpdate",
      key: "lastVideoUpdate",
      render: (val: string) => (val ? new Date(val).toLocaleDateString("fr-FR") : "--"),
    },
  ];

  const providerColumns = [
    { title: "Pays", dataIndex: "countryName", key: "countryName" },
    { title: "Code", dataIndex: "countryCode", key: "countryCode" },
    { title: "Fournisseur", dataIndex: "paymentProvider", key: "paymentProvider" },
    {
      // Un agregateur comme PayDunya dessert plusieurs portefeuilles : sans
      // cette colonne, ses lignes portaient toutes le meme nom et rien ne
      // permettait de savoir laquelle activer.
      title: "Moyen de paiement",
      dataIndex: "walletCode",
      key: "walletCode",
      render: (val: string | undefined, row: PaymentProviderConfig) =>
        val ? <Tag color="purple">{val}</Tag> : <Text type="secondary">{row.displayName ?? "—"}</Text>,
    },
    { title: "Devise", dataIndex: "currency", key: "currency" },
    {
      title: "Priorité",
      dataIndex: "priority",
      key: "priority",
      render: (val: number) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "Actif",
      dataIndex: "isActive",
      key: "isActive",
      render: (val: boolean) =>
        val ? <Tag color="green">Actif</Tag> : <Tag color="default">Inactif</Tag>,
    },
  ];

  return (
    <div>
      <Title level={2}>Paramètres Techniques</Title>
      <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
        Surveillance du système, configuration et état des services
      </Text>

      {/* Section 1: Health */}
      <Card
        title={
          <span>
            <HeartOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Santé du système
          </span>
        }
        extra={
          <Tooltip title="Actualiser">
            <Button icon={<ReloadOutlined />} onClick={loadHealth} loading={healthLoading} />
          </Tooltip>
        }
        style={{ marginBottom: 24 }}
      >
        <Spin spinning={healthLoading}>
          {healthError ? (
            <Empty
              description="Impossible de récupérer l'état de santé des services."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : healthData ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <Text>Statut global : </Text>
                {healthData.status === "UP" ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    {healthData.total} service(s) en marche
                  </Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />} color="error">
                    {healthData.down} service(s) en panne sur {healthData.total}
                  </Tag>
                )}
              </div>
              {healthComponents.length > 0 && (
                <Table
                  columns={healthColumns}
                  dataSource={healthComponents}
                  rowKey="key"
                  pagination={false}
                  size="small"
                />
              )}
            </>
          ) : null}
        </Spin>
      </Card>

      {/* Section 2: System Configuration */}
      <Card
        title={
          <span>
            <CloudServerOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            Configuration système
          </span>
        }
        style={{ marginBottom: 24 }}
      >
        {/* Ces valeurs etaient ecrites en dur, et « Stockage media : MinIO »
            etait devenu faux depuis le passage a Cloudflare R2 le 15/09. Elles
            viennent desormais de la configuration du serveur : une seule place
            a corriger le jour ou elles changent. */}
        <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
          <Descriptions.Item label="API Gateway">
            {healthData?.infrastructure?.apiGateway ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Authentification">
            {healthData?.infrastructure?.authentication ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Stockage média">
            {healthData?.infrastructure?.mediaStorage ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Base de données">
            {healthData?.infrastructure?.database ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Cache">
            {healthData?.infrastructure?.cache ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Environnement">
            {healthData?.infrastructure?.environment ? (
              <Tag color="blue">{healthData.infrastructure.environment}</Tag>
            ) : (
              "—"
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Section 3: Payment Providers Overview */}
      <Card
        title={
          <span>
            <CreditCardOutlined style={{ marginRight: 8, color: "#722ed1" }} />
            Fournisseurs de paiement configurés
          </span>
        }
      >
        <Spin spinning={providersLoading}>
          {providers.length > 0 ? (
            <Table
              columns={providerColumns}
              dataSource={providers}
              rowKey="id"
              pagination={false}
              size="small"
            />
          ) : (
            <Empty
              description="Aucun fournisseur de paiement configuré"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Spin>
      </Card>

      {/* Section 4: Media Processing Status */}
      <Card
        title={
          <span>
            <VideoCameraOutlined style={{ marginRight: 8, color: "#faad14" }} />
            Traitement média
          </span>
        }
        extra={
          <Space>
            <Select
              placeholder="Filtrer par statut"
              allowClear
              style={{ width: 180 }}
              value={processingFilter}
              onChange={setProcessingFilter}
              options={[
                { label: "En attente", value: "PENDING" },
                { label: "En cours", value: "PROCESSING" },
                { label: "Terminé", value: "COMPLETED" },
                { label: "Échoué", value: "FAILED" },
              ]}
            />
            <Tooltip title="Actualiser">
              <Button icon={<ReloadOutlined />} onClick={loadContentsProcessing} loading={contentsLoading} />
            </Tooltip>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Spin spinning={contentsLoading}>
          {filteredContents.length > 0 ? (
            <Table
              columns={processingColumns}
              dataSource={filteredContents}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} contenus`,
              }}
              size="small"
            />
          ) : (
            <Empty
              description="Aucun contenu trouvé"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default TechnicalSettingsPage;
